import { createElement, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search, X } from 'lucide-react'
import { CATEGORY_ICON_LABELS } from '../../../shared/categoryCatalog'
import { isIconNode, type IconNode } from '../../../shared/iconDrawing'

export type IconChoice = { key: string; name: string; node: IconNode | null }

// The app's own sports shoe, drawn as in constants/categories.ts.
export const shoeNode: IconNode = [['path', { d: 'm15 10.42 4.8-5.07M19 18h3M9.5 22 21.414 9.415A2 2 0 0 0 21.2 6.4l-5.61-4.208A1 1 0 0 0 14 3v2a2 2 0 0 1-1.394 1.906L8.677 8.053A1 1 0 0 0 8 9c-.155 6.393-2.082 9-4 9a2 2 0 0 0 0 4h14' }]]

const PAGE_SIZE = 80
// A built-in icon by its Portuguese name; any other as the grid names it.
const label = (key: string, icons: IconChoice[] = []) => (CATEGORY_ICON_LABELS as Record<string, string>)[key]
  ?? icons.find((icon) => icon.key === key)?.name ?? key.replace(/([A-Za-z0-9])(?=[A-Z0-9])/g, '$1-').toLowerCase()
const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

// As Lucide's own search ranks: the name itself, then a whole word of it,
// then a word that starts with the search, then anything containing it.
function rankOf(icon: IconChoice, search: string) {
  const words = icon.name.split('-')
  if (icon.name === search) return 0
  if (words.includes(search)) return 1
  return words.some((word) => word.startsWith(search)) ? 2 : 3
}

// The Lucide library, loaded only when the picker first opens.
let catalogue: Promise<IconChoice[]> | null = null
function loadIcons() {
  if (!catalogue) {
    catalogue = import('../data/lucideIcons.json')
      .then((data) => [
        ...data.icons.filter((icon) => isIconNode(icon.node)) as IconChoice[],
        { key: 'SportShoe', name: 'sport-shoe', node: shoeNode },
      ])
      .catch((error) => {
        catalogue = null
        throw error
      })
  }
  return catalogue
}

export function IconDrawing({ node, size = 20 }: { node: IconNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {node.map(([tag, attributes], index) => createElement(tag, { ...attributes, key: index }))}
    </svg>
  )
}

type Props = {
  value: string
  wide?: boolean
  preview: React.ReactNode
  onChange: (icon: IconChoice) => void
}

// Search by name and pick from a grid, as Lucide's own site does.
export function IconPicker({ value, wide = false, preview, onChange }: Props) {
  const trigger = useRef<HTMLButtonElement>(null)
  const dialog = useRef<HTMLDivElement>(null)
  const searchInput = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [icons, setIcons] = useState<IconChoice[]>([])
  const [error, setError] = useState('')
  const [limit, setLimit] = useState(PAGE_SIZE)

  const matches = useMemo(() => {
    const words = normalize(query.trim()).split(/\s+/).filter(Boolean)
    const search = words.join('-')
    return icons
      .filter((icon) => words.every((word) => normalize(`${icon.name} ${icon.key} ${label(icon.key)}`).includes(word)))
      .sort((a, b) => rankOf(a, search) - rankOf(b, search) || a.name.length - b.name.length || a.name.localeCompare(b.name))
  }, [icons, query])

  const retry = () => {
    setError('')
    void loadIcons().then(setIcons).catch(() => setError('Não foi possível carregar os ícones.'))
  }

  useEffect(() => {
    if (!open) return
    let canceled = false
    setError('')
    void loadIcons()
      .then((items) => { if (!canceled) setIcons(items) })
      .catch(() => { if (!canceled) setError('Não foi possível carregar os ícones.') })
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    searchInput.current?.focus()
    return () => {
      canceled = true
      document.body.style.overflow = previousOverflow
      trigger.current?.focus()
    }
  }, [open])

  // Escape closes and Tab stays inside the dialog.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      setOpen(false)
    }
    if (event.key !== 'Tab') return
    const controls = [...dialog.current!.querySelectorAll<HTMLElement>('button:not(:disabled), input')]
    const first = controls[0]
    const last = controls.at(-1)
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  return (
    <>
      <div className={wide ? 'field field--wide' : 'field'}>
        <span>Ícone</span>
        <button ref={trigger} type="button" className="icon-picker-trigger" aria-label="Escolher ícone" aria-haspopup="dialog"
          onClick={() => { setQuery(''); setLimit(PAGE_SIZE); setOpen(true) }}>
          {preview}<span>{label(value, icons)}</span><ChevronDown size={16} />
        </button>
      </div>
      {open && createPortal(
        <div className="dialog-backdrop" onClick={() => setOpen(false)}>
          <div ref={dialog} className="dialog icon-picker" role="dialog" aria-modal="true" aria-labelledby="icon-picker-title"
            onClick={(event) => event.stopPropagation()} onKeyDown={onKeyDown}>
            <div className="dialog__heading">
              <div>
                <h2 id="icon-picker-title">Escolher ícone</h2>
                <p>Busque pelo nome e escolha o desenho.</p>
              </div>
              <button type="button" className="icon-button" aria-label="Fechar ícones" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <label className="icon-picker-search">
              <Search size={18} />
              <input ref={searchInput} type="search" aria-label="Buscar ícones" placeholder="Buscar ícones: car, star…" value={query}
                onChange={(event) => { setQuery(event.target.value); setLimit(PAGE_SIZE) }} />
            </label>
            {error ? (
              <p className="form-error" role="alert">{error} <button type="button" className="text-button" onClick={retry}>Tentar novamente</button></p>
            ) : icons.length === 0 ? (
              <p role="status">Carregando ícones…</p>
            ) : (
              <p className="field-hint" aria-live="polite">{matches.length === 1 ? '1 ícone' : `${matches.length} ícones`}</p>
            )}
            <div className="icon-picker-results">
              {icons.length > 0 && matches.length === 0 && <p>Nenhum ícone encontrado. Tente outro nome.</p>}
              <div className="icon-picker-grid">
                {matches.slice(0, limit).map((icon) => (
                  <button key={icon.key} type="button" className="icon-picker-option" aria-label={`Selecionar ${icon.name}`} aria-pressed={value === icon.key}
                    onClick={() => { onChange(icon); setOpen(false) }}>
                    <IconDrawing node={icon.node!} size={26} /><span>{icon.name}</span>
                  </button>
                ))}
              </div>
              {matches.length > limit && (
                <button type="button" className="button button--secondary icon-picker-more" onClick={() => setLimit((previous) => previous + PAGE_SIZE)}>
                  Mostrar mais ícones
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
